// Batching for POST /v1/events (Milestone 7.3, brief §7).
//
// The brief's requirement is explicit: do not send one network request per
// event. A single ad break can produce a dozen publisher events, and
// PLAYBACK_PROGRESS alone fires several times a second - one request each
// would be untenable.
//
// Bounded by construction: `maxQueueSize` caps memory, and an overflow drops
// the OLDEST events. Telemetry is lossy by contract, and the newest events are
// the ones a live dashboard cares about - so dropping the head is the honest
// failure mode, and it is counted so the loss is visible rather than silent.
export class EventQueue {
  #events = [];
  #batchSize;
  #maxQueueSize;
  #flushIntervalMs;
  #flush;
  #timer = null;
  #droppedCount = 0;
  #stopped = false;

  constructor({ batchSize, maxQueueSize, flushIntervalMs, onFlush }) {
    this.#batchSize = batchSize;
    this.#maxQueueSize = maxQueueSize;
    this.#flushIntervalMs = flushIntervalMs;
    this.#flush = onFlush;
  }

  get size() {
    return this.#events.length;
  }

  get droppedCount() {
    return this.#droppedCount;
  }

  enqueue(event) {
    if (this.#stopped) return;

    this.#events.push(event);

    if (this.#events.length > this.#maxQueueSize) {
      const overflow = this.#events.length - this.#maxQueueSize;
      this.#events.splice(0, overflow);
      this.#droppedCount += overflow;
    }

    if (this.#events.length >= this.#batchSize) {
      // Fire immediately on a full batch rather than waiting for the timer,
      // so a burst of events is not held back by the flush interval.
      this.flush();
      return;
    }

    this.#scheduleFlush();
  }

  // Always resolves. A flush failure must never surface to the caller, which
  // is on the playback path - the whole point of fire-and-forget isolation.
  async flush() {
    this.#clearTimer();
    if (this.#events.length === 0) return;

    const batch = this.#events.splice(0, this.#events.length);

    try {
      await this.#flush(batch);
    } catch {
      // Deliberately swallowed and NOT re-queued. Re-queueing a failing batch
      // grows the queue without bound during an outage and replays the same
      // failure forever; RetryPolicy already gave it its allotted attempts.
    }
  }

  // Called on destroy: stop accepting new events, then make a final attempt to
  // deliver whatever is queued.
  async stop() {
    this.#stopped = true;
    this.#clearTimer();
    await this.flush();
  }

  #scheduleFlush() {
    if (this.#timer !== null) return;
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.flush();
    }, this.#flushIntervalMs);
  }

  #clearTimer() {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }
}
